/**
 * Follow-up Template Service - Template operations
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Get available templates for entity type and status
 * @param {string} entityType - Entity type
 * @param {string} [entityStatus] - Entity status
 * @returns {Promise<Object>} Available templates with success/error structure
 */
export const getTemplates = async (entityType, entityStatus = null) => {
  try {
    const params = new URLSearchParams({ entityType });
    if (entityStatus) {
      params.append("entityStatus", entityStatus);
    }

    const response = await api.get(`/followups/templates?${params.toString()}`);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data?.templates || response.data || [],
      };
    }
    return {
      success: true,
      data: [],
    };
  } catch (error) {
    logError("Error fetching templates", {
      entityType,
      entityStatus,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to fetch templates",
      data: [],
    };
  }
};

/**
 * Generate template message
 * @param {Object} options - Generation options
 * @param {string} [options.followUpId] - Follow-up ID
 * @param {string} options.templateType - Template type
 * @param {string} [options.entityType] - Entity type
 * @param {string} [options.entityStatus] - Entity status
 * @param {Object} [options.entityContext] - Entity context
 * @returns {Promise<Object>} Generated message with success/error structure
 */
export const generateTemplate = async (options) => {
  try {
    const response = await api.post("/followups/templates/generate", options);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data?.message || response.data || "",
      };
    }
    return {
      success: true,
      data: response.data?.message || "",
    };
  } catch (error) {
    logError("Error generating template", {
      options,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to generate template",
      data: null,
    };
  }
};

